import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { ExternalLink, X } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import SearchableSelectInput from "./searchable-select-input";
import EntityAttachmentsList from "./entity-attachments-list";
import ExternalDocumentsSection from "./external-documents-section";
import { productOrderItemsTableColumns } from "../lib/table-columns";
import {
  ProductOrder,
  deleteProductOrderDocument,
  getInvoices,
  getProductOrder,
  getQuotations,
  setProductOrderInvoices,
  setProductOrderQuotations,
  uploadProductOrderDocument,
} from "../lib/api";
import { getBackendErrorMessage, getBadgeVariant } from "../lib/utils";
import SidePanel, { PanelHeader } from "./side-panel";
import DataTable from "./data-table";
import { useIsMobile } from "../hooks/use-is-mobile";

interface ProductOrderDetailsDrawerProps {
  orderId: string | null;
  onClose: () => void;
}

const toNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (n: number | null | undefined) => {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const getItemUnitPrice = (item: ProductOrder["items"][number]) =>
  toNumber(item.unitPrice ?? item.unit_price);

const getItemLineTotal = (item: ProductOrder["items"][number]) => {
  const explicit = item.lineTotal ?? item.line_total;
  if (explicit != null) return toNumber(explicit);
  return toNumber(item.quantity) * getItemUnitPrice(item);
};

const ProductOrderDetailsDrawer: React.FC<ProductOrderDetailsDrawerProps> = ({
  orderId,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [linkInvoiceId, setLinkInvoiceId] = useState("");
  const [linkQuotationId, setLinkQuotationId] = useState("");
  const [isSavingInvoices, setIsSavingInvoices] = useState(false);
  const [isSavingQuotation, setIsSavingQuotation] = useState(false);

  const orderQuery = useQuery({
    queryKey: ["shop", "orders", orderId],
    queryFn: async () => getProductOrder(orderId!),
    enabled: !!orderId,
  });

  const invoicesQuery = useQuery({
    queryKey: ["invoices", "shop-order-link", { page: 1, limit: 100 }],
    queryFn: async () => getInvoices(1, 100, ""),
    enabled: !!orderId,
  });

  const quotationsQuery = useQuery({
    queryKey: ["quotations", "shop-order-link", { page: 1, limit: 100 }],
    queryFn: async () => getQuotations(1, 100, ""),
    enabled: !!orderId,
  });

  useEffect(() => {
    if (!orderId) return;
    setLinkInvoiceId("");
    setLinkQuotationId("");
  }, [orderId]);

  useEffect(() => {
    if (!orderQuery.error) return;
    toast.error(
      getBackendErrorMessage(orderQuery.error, "Failed to load order"),
    );
  }, [orderQuery.error]);

  const order: ProductOrder | null = orderQuery.data ?? null;
  const isCancelled = order?.status === "CANCELLED";

  const linkedInvoiceIds = useMemo(
    () => new Set((order?.invoices ?? []).map((inv) => inv.id)),
    [order?.invoices],
  );

  const linkedQuotations = useMemo(
    () =>
      order?.quotations?.length
        ? order.quotations
        : order?.quotation
          ? [order.quotation]
          : [],
    [order?.quotations, order?.quotation],
  );

  const linkedQuotationIds = useMemo(
    () => new Set(linkedQuotations.map((q) => q.id)),
    [linkedQuotations],
  );

  const availableInvoiceOptions = useMemo(
    () =>
      (invoicesQuery.data?.data ?? [])
        .filter((inv) => !linkedInvoiceIds.has(inv.id))
        .map((inv) => ({
          value: inv.id,
          label: `${inv.reference} — ${inv.customer?.fullName ?? "Customer"}`,
        })),
    [invoicesQuery.data, linkedInvoiceIds],
  );

  const availableQuotationOptions = useMemo(
    () =>
      (quotationsQuery.data?.data ?? [])
        .filter((q) => !linkedQuotationIds.has(q.id))
        .map((q) => ({
          value: q.id,
          label: `${q.reference} — ${q.customer?.fullName ?? "Customer"}`,
        })),
    [quotationsQuery.data, linkedQuotationIds],
  );

  const itemRows = (order?.items ?? []).map((item) => ({
    id: item.id,
    product: item.product?.name ?? item.productName ?? "—",
    order_type: item.orderType,
    quantity: String(item.quantity ?? "—"),
    days_hired:
      item.orderType === "RENT"
        ? String(item.daysHired ?? item.days_hired ?? "—")
        : "—",
    unit_price: formatMoney(getItemUnitPrice(item)),
    line_total: formatMoney(getItemLineTotal(item)),
  }));

  const orderTotal = (order?.items ?? []).reduce(
    (sum, item) => sum + getItemLineTotal(item),
    0,
  );

  const refreshOrder = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["shop", "orders", orderId] }),
      queryClient.invalidateQueries({ queryKey: ["shop", "orders"] }),
    ]);
  };

  const handleLinkInvoice = async () => {
    if (!order?.id || !linkInvoiceId) return;
    setIsSavingInvoices(true);
    try {
      const nextIds = [
        ...(order.invoices ?? []).map((i) => i.id),
        linkInvoiceId,
      ];
      await setProductOrderInvoices(order.id, nextIds);
      toast.success("Invoice linked");
      setLinkInvoiceId("");
      await refreshOrder();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to link invoice"));
    } finally {
      setIsSavingInvoices(false);
    }
  };

  const handleUnlinkInvoice = async (invoiceId: string) => {
    if (!order?.id) return;
    setIsSavingInvoices(true);
    try {
      const nextIds = (order.invoices ?? [])
        .map((i) => i.id)
        .filter((id) => id !== invoiceId);
      await setProductOrderInvoices(order.id, nextIds);
      toast.success("Invoice unlinked");
      await refreshOrder();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to unlink invoice"));
    } finally {
      setIsSavingInvoices(false);
    }
  };

  const handleUnlinkAllInvoices = async () => {
    if (!order?.id) return;
    setIsSavingInvoices(true);
    try {
      await setProductOrderInvoices(order.id, []);
      toast.success("All invoices unlinked");
      await refreshOrder();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to unlink invoices"));
    } finally {
      setIsSavingInvoices(false);
    }
  };

  const handleLinkQuotation = async () => {
    if (!order?.id || !linkQuotationId) return;
    setIsSavingQuotation(true);
    try {
      const nextIds = [...linkedQuotations.map((q) => q.id), linkQuotationId];
      await setProductOrderQuotations(order.id, nextIds);
      toast.success("Quotation linked");
      setLinkQuotationId("");
      await refreshOrder();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to link quotation"));
    } finally {
      setIsSavingQuotation(false);
    }
  };

  const handleUnlinkQuotation = async (quotationId: string) => {
    if (!order?.id) return;
    setIsSavingQuotation(true);
    try {
      const nextIds = linkedQuotations
        .map((q) => q.id)
        .filter((id) => id !== quotationId);
      await setProductOrderQuotations(order.id, nextIds);
      toast.success("Quotation unlinked");
      await refreshOrder();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to unlink quotation"));
    } finally {
      setIsSavingQuotation(false);
    }
  };

  const handleUnlinkAllQuotations = async () => {
    if (!order?.id) return;
    setIsSavingQuotation(true);
    try {
      await setProductOrderQuotations(order.id, []);
      toast.success("All quotations unlinked");
      await refreshOrder();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to unlink quotations"));
    } finally {
      setIsSavingQuotation(false);
    }
  };

  return (
    <SidePanel open={!!orderId} onClose={onClose} width="w-[720px]">
      {orderId && (
        <div className="relative h-full flex flex-col">
          {isMobile ? (
            <PanelHeader
              title="Order Details"
              subtitle={order?.reference}
              onClose={onClose}
            />
          ) : (
            <div className="px-6 py-4 flex-shrink-0 border-b border-gray-100">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-[22px] font-semibold text-foreground font-jakarta">
                    Order Details
                  </h2>
                  {order && (
                    <p className="text-sm text-gray-500 mt-1">
                      {order.reference}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  className="h-8 w-8 p-0 flex-shrink-0"
                  onClick={onClose}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <ScrollArea className="flex-1">
            <div
              className={
                isMobile ? "p-4 pb-8 space-y-6" : "px-6 py-4 space-y-6"
              }
            >
              {orderQuery.isLoading ? (
                <p className="text-sm text-gray-500">Loading order...</p>
              ) : order ? (
                <>
                  {isMobile ? (
                    <dl className="divide-y divide-gray-100 rounded-2xl border border-gray-100 bg-white">
                      <div className="flex items-center justify-between gap-4 px-4 py-3">
                        <dt className="shrink-0 text-sm text-gray-500">
                          Status
                        </dt>
                        <dd>
                          <Badge variant={getBadgeVariant(order.status)}>
                            {order.status}
                          </Badge>
                        </dd>
                      </div>
                      <div className="flex items-start justify-between gap-4 px-4 py-3">
                        <dt className="shrink-0 text-sm text-gray-500">
                          Created
                        </dt>
                        <dd className="min-w-0 text-right text-sm font-medium text-gray-900">
                          {format(
                            new Date(order.createdAt),
                            "MMM dd, yyyy HH:mm",
                          )}
                        </dd>
                      </div>
                      <div className="flex items-start justify-between gap-4 px-4 py-3">
                        <dt className="shrink-0 text-sm text-gray-500">
                          Customer
                        </dt>
                        <dd className="min-w-0 break-words text-right text-sm font-medium text-gray-900">
                          {order.user?.fullName ?? "—"}
                          {order.user?.email && (
                            <p className="break-all text-xs font-normal text-gray-500">
                              {order.user.email}
                            </p>
                          )}
                        </dd>
                      </div>
                      <div className="flex items-start justify-between gap-4 px-4 py-3">
                        <dt className="shrink-0 text-sm text-gray-500">
                          Location
                        </dt>
                        <dd className="min-w-0 break-words text-right text-sm font-medium text-gray-900">
                          {order.location || "—"}
                        </dd>
                      </div>
                    </dl>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Status
                        </p>
                        <Badge
                          variant={getBadgeVariant(order.status)}
                          className="mt-1"
                        >
                          {order.status}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Created
                        </p>
                        <p className="text-sm font-medium mt-1">
                          {format(
                            new Date(order.createdAt),
                            "MMM dd, yyyy HH:mm",
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Customer
                        </p>
                        <p className="text-sm font-medium mt-1">
                          {order.user?.fullName ?? "—"}
                        </p>
                        {order.user?.email && (
                          <p className="text-xs text-gray-500">
                            {order.user.email}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Location
                        </p>
                        <p className="text-sm font-medium mt-1">
                          {order.location || "—"}
                        </p>
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-base font-semibold mb-3">Line Items</h3>
                    {isMobile ? (
                      <DataTable
                        columns={productOrderItemsTableColumns}
                        data={itemRows}
                      />
                    ) : (
                      <div className="rounded-md border border-gray-100 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                              {productOrderItemsTableColumns.map((col) => (
                                <th
                                  key={col.accessor}
                                  className="px-3 py-2 text-left font-medium text-gray-600"
                                >
                                  {col.header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {itemRows.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={productOrderItemsTableColumns.length}
                                  className="px-3 py-4 text-center text-gray-500"
                                >
                                  No items
                                </td>
                              </tr>
                            ) : (
                              itemRows.map((row) => (
                                <tr
                                  key={row.id}
                                  className="border-b border-gray-50 last:border-0"
                                >
                                  <td className="px-3 py-2">{row.product}</td>
                                  <td className="px-3 py-2">
                                    {row.order_type}
                                  </td>
                                  <td className="px-3 py-2">{row.quantity}</td>
                                  <td className="px-3 py-2">
                                    {row.days_hired}
                                  </td>
                                  <td className="px-3 py-2">
                                    {row.unit_price}
                                  </td>
                                  <td className="px-3 py-2">
                                    {row.line_total}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <p className="text-sm font-medium text-right mt-2 text-gray-700">
                      Total: {formatMoney(orderTotal)}
                    </p>
                  </div>

                  <div className="space-y-3 pt-2 border-t border-gray-100">
                    <h3 className="text-base font-semibold">Attachments</h3>
                    <EntityAttachmentsList attachments={order.attachments} />
                  </div>

                  <div className="space-y-3 pt-2 border-t border-gray-100">
                    <h3 className="text-base font-semibold">Invoices</h3>
                    <p className="text-xs text-gray-500">
                      Link an existing system invoice, or upload an invoice
                      file.
                    </p>
                    {(order.invoices ?? []).length > 0 ? (
                      <div className="space-y-2">
                        {order.invoices.map((invoice) => (
                          <div
                            key={invoice.id}
                            className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50"
                          >
                            <div>
                              <p className="text-sm font-medium">
                                {invoice.reference}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Linked system invoice
                              </p>
                              {invoice.pdfUrl && (
                                <a
                                  href={invoice.pdfUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1 mt-1"
                                >
                                  View PDF <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                            {!isCancelled && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-200 text-red-700 hover:bg-red-50"
                                disabled={isSavingInvoices}
                                onClick={() =>
                                  void handleUnlinkInvoice(invoice.id)
                                }
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                        ))}
                        {!isCancelled && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-700 hover:bg-red-50"
                            disabled={isSavingInvoices}
                            onClick={() => void handleUnlinkAllInvoices()}
                          >
                            Unlink all
                          </Button>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        No system invoices linked.
                      </p>
                    )}

                    {!isCancelled && (
                      <div className="space-y-3 pt-2">
                        <p className="text-sm font-medium text-gray-700">
                          Link existing invoice
                        </p>
                        <div
                          className={
                            isMobile ? "w-full" : "min-w-[280px] max-w-md"
                          }
                        >
                          <SearchableSelectInput
                            options={availableInvoiceOptions}
                            value={linkInvoiceId}
                            defaultLabel={
                              invoicesQuery.isLoading
                                ? "Loading invoices..."
                                : availableInvoiceOptions.length === 0
                                  ? "No more invoices to add"
                                  : "Select an invoice"
                            }
                            onSelect={setLinkInvoiceId}
                          />
                        </div>
                        <Button
                          size="sm"
                          disabled={
                            isSavingInvoices ||
                            invoicesQuery.isLoading ||
                            !linkInvoiceId ||
                            availableInvoiceOptions.length === 0
                          }
                          onClick={() => void handleLinkInvoice()}
                        >
                          {isSavingInvoices ? "Saving..." : "Link invoice"}
                        </Button>
                      </div>
                    )}

                    <ExternalDocumentsSection
                      title="Uploaded invoice files"
                      kind="INVOICE"
                      documents={order.uploadedInvoices ?? []}
                      canUpload={!isCancelled}
                      onUpload={async (file) => {
                        await uploadProductOrderDocument(
                          order.id,
                          "INVOICE",
                          file,
                        );
                        await refreshOrder();
                      }}
                      onDelete={async (documentId) => {
                        await deleteProductOrderDocument(order.id, documentId);
                        await refreshOrder();
                      }}
                    />
                  </div>

                  <div className="space-y-3 pt-2 border-t border-gray-100">
                    <h3 className="text-base font-semibold">Quotations</h3>
                    <p className="text-xs text-gray-500">
                      Link existing system quotations, or upload a quotation
                      file.
                    </p>
                    {linkedQuotations.length > 0 ? (
                      <div className="space-y-2">
                        {linkedQuotations.map((quotation) => (
                          <div
                            key={quotation.id}
                            className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50"
                          >
                            <div>
                              <p className="text-sm font-medium">
                                {quotation.reference}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Linked system quotation
                              </p>
                              {quotation.pdfUrl && (
                                <a
                                  href={quotation.pdfUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1 mt-1"
                                >
                                  View PDF <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                            {!isCancelled && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-200 text-red-700 hover:bg-red-50"
                                disabled={isSavingQuotation}
                                onClick={() =>
                                  void handleUnlinkQuotation(quotation.id)
                                }
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                        ))}
                        {!isCancelled && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-700 hover:bg-red-50"
                            disabled={isSavingQuotation}
                            onClick={() => void handleUnlinkAllQuotations()}
                          >
                            Unlink all
                          </Button>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        No system quotations linked.
                      </p>
                    )}

                    {!isCancelled && (
                      <div className="space-y-3 pt-2">
                        <p className="text-sm font-medium text-gray-700">
                          Link existing quotation
                        </p>
                        <div
                          className={
                            isMobile ? "w-full" : "min-w-[280px] max-w-md"
                          }
                        >
                          <SearchableSelectInput
                            options={availableQuotationOptions}
                            value={linkQuotationId}
                            defaultLabel={
                              quotationsQuery.isLoading
                                ? "Loading quotations..."
                                : availableQuotationOptions.length === 0
                                  ? "No more quotations to add"
                                  : "Select a quotation"
                            }
                            onSelect={setLinkQuotationId}
                          />
                        </div>
                        <Button
                          size="sm"
                          disabled={
                            isSavingQuotation ||
                            quotationsQuery.isLoading ||
                            !linkQuotationId ||
                            availableQuotationOptions.length === 0
                          }
                          onClick={() => void handleLinkQuotation()}
                        >
                          {isSavingQuotation ? "Saving..." : "Link quotation"}
                        </Button>
                      </div>
                    )}

                    <ExternalDocumentsSection
                      title="Uploaded quotation files"
                      kind="QUOTATION"
                      documents={order.uploadedQuotations ?? []}
                      canUpload={!isCancelled}
                      onUpload={async (file) => {
                        await uploadProductOrderDocument(
                          order.id,
                          "QUOTATION",
                          file,
                        );
                        await refreshOrder();
                      }}
                      onDelete={async (documentId) => {
                        await deleteProductOrderDocument(order.id, documentId);
                        await refreshOrder();
                      }}
                    />
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">Order not found.</p>
              )}
            </div>
          </ScrollArea>
        </div>
      )}
    </SidePanel>
  );
};

export default ProductOrderDetailsDrawer;
